/* eslint-disable */

import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    Avatar,
} from '@mui/material';
import {
    CombineData,
    CustomButton,
    CustomModal,
    SmallNotes,
} from '../../../shared';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    DownloadIcon,
} from '../../../../assets';
import { useDispatch, useSelector } from 'react-redux';
import TableRowsSkeleton from '../../../shared/skelton/tableSkelton';
import { CSVLink } from 'react-csv';
import engagementService from '../../../../services/engagement';
import { actions as digitalEngagementActions } from '../../../../redux/reducers/digitalEngagementReducer';
import {
    calculateMaxWidth,
    formatDateForExport,
    getSortedData,
    handleHideSpecialtyColumns,
    handleRowClassForEngagementsTable,
} from '../../../../utility';
import {
    hideDownloadButtonCompanies,
    REGENERON_COMPANY_ID,
} from '../../../../constants';
import regenronData from '../../../../constants/regenronData.json';
import { actions as prescriptionActions } from '../../../../redux/reducers/prescriptionReducer';
import PrescribersProfile from '../../dashboard/topPrescribers/prescriberProfile';
import {
    handleIcons,
    showDigitalEngagementPriorityData,
    showExtraColumns,
} from '../../../../utility/companySpecificFunctions';
import {
    showDistrict,
    showTerritories,
    showSamTerritories,
    notes,
    hideDistrict,
} from './constants';

function NPITable() {
    const dispatch = useDispatch();
    const {
        providers: providersOnPage,
        isLoading,
        isProvidersLoading,
        currentPage,
        pageSize,
        serverPaginated,
        totalPages,
        totalRecordsFetch,
        filteredProviders,
    } = useSelector((state) => state.digitalEngagement);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const {
        userDetails: { is_admin = false },
    } = useSelector((state) => state.user);
    const { setCurrentPage } = digitalEngagementActions;
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCombineModal, setShowCombineModal] = useState(false);
    const [combineData, setCombineData] = useState({});
    const { setSelectedNPI } = prescriptionActions;
    const [maxWidth, setMaxWidth] = useState(calculateMaxWidth());
    const showFMT = showExtraColumns(company.id);
    const showDigitalEngagementData = showDigitalEngagementPriorityData(
        company.id,
    );
    const isDistrictVisible = showDistrict.includes(company.id);
    const isTerritoryVisible = showTerritories.includes(company.id);
    const showAssets = [161, 67, 140].includes(company.id);
    const showTier = [58, 161].includes(company.id);
    const showAffiliationType = [109].includes(company.id);
    const hideNpi_id = [97].includes(company.id);
    const only_state_zip = [97].includes(company.id);

    const physician_segment_titles = {
        97: 'Segments',
        default: 'Physician Segments',
    };

    const physician_segment_title =
        physician_segment_titles[company.id] ||
        physician_segment_titles['default'];

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };
    const sortedData = React.useMemo(
        () => getSortedData(orderBy, order, filteredProviders),
        [order, orderBy, filteredProviders],
    );
    const handlePageChange = (event, page) => {
        dispatch(setCurrentPage(page));
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    };
    // rowsOverride lets "Export All Data" build the file from the full provider
    // set fetched on demand, rather than from `providers`, which under server-side
    // pagination holds only the ten rows currently on screen.
    const [isExporting, setIsExporting] = useState(false);

    // "Export All Data" must mean all of it. The table is paginated server-side,
    // so the rows in Redux are one page; this pulls the complete, enriched set
    // from the dashboard endpoint (which returns every provider when no page
    // parameters are sent) and builds the file from that.
    const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const text = Array.isArray(value) ? value.join(', ') : String(value);
        return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
    };

    const handleExportAll = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const response = await engagementService.digitalEngagement(
                company.id,
                startDateRange,
                endDateRange,
            );
            const allRows = response?.data?.engagement?.fmt_provider ?? [];
            const { data, headers, filename } = handleCSVData(allRows);
            const csv = [
                headers.map((h) => escapeCsv(h.label)).join(','),
                ...data.map((row) =>
                    headers.map((h) => escapeCsv(row[h.key])).join(','),
                ),
            ].join('\n');
            const url = URL.createObjectURL(
                new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleCSVData = (rowsOverride) => {
        const providers = rowsOverride || providersOnPage;
        if (company.id === REGENERON_COMPANY_ID) {
            const csvName = 'FMT Match_NPI List_08.31.2024';
            const csvData = [];
            const headers = [
                { label: 'Total NPI #', key: 'Total NPI #' },
                { label: 'FMT Match NPIs', key: 'FMT Match NPIs' },
                {
                    label: 'No of unique banners engaged across DLBCL and FL',
                    key: 'No of unique banners engaged across DLBCL and FL',
                },
                {
                    label: 'Type of Banners Topics Engaged',
                    key: 'Type of Banners Topics Engaged',
                },
                {
                    label: 'Type of Asset Reviewed Most on Web Portal',
                    key: 'Type of Asset Reviewed Most on Web Portal',
                },
            ];

            regenronData?.map((physician) => {
                const data = {
                    'Total NPI #': physician['Total NPI #'],
                    'FMT Match NPIs': physician['FMT Match NPIs'],
                    'No of unique banners engaged across DLBCL and FL':
                        physician[
                            'No of unique banners engaged across DLBCL and FL'
                        ],
                    'Type of Banners Topics Engaged':
                        physician['Type of Banners Topics Engaged'],
                    'Type of Asset Reviewed Most on Web Portal':
                        physician['Type of Asset Reviewed Most on Web Portal'],
                };
                csvData.push(data);
            });
            return {
                data: csvData,
                headers: headers,
                filename: csvName + '.csv',
                target: '_blank',
            };
        } else {
            const csvName = 'Digital Engagement';
            const isHidden = handleHideSpecialtyColumns(company.id);
            const csvData = [];
            const headers = [
                ...(hideNpi_id ? [] : [{ label: 'NPI', key: 'npi' }]),
                { label: 'Name', key: 'name' },
                { label: 'Location', key: 'location' },
                ...(showDistrict.includes(company.id)
                    ? [{ label: 'Region - RMD', key: 'district' }]
                    : []),
                ...(showTerritories.includes(company.id)
                    ? [{ label: 'Territory-IROC', key: 'territory' }]
                    : []),
                ...(showSamTerritories.includes(company.id)
                    ? [{ label: 'Territory-SAM', key: 'territory1' }]
                    : []),
                ...(isHidden ? [] : [{ label: 'Specialty', key: 'specialty' }]),
                { label: physician_segment_title, key: 'physicianSegment' },
                ...(showAffiliationType
                    ? [{ label: 'Affiliation Type', key: 'affiliationType' }]
                    : []),
                ...(showTier ? [{ label: 'Tier', key: 'tier' }] : []),
                ...(showFMT || showDigitalEngagementData
                    ? [
                          {
                              label: 'Digital Engagement',
                              key: 'digital_engagement_priority',
                          },
                      ]
                    : []),
                ...(showFMT
                    ? [{ label: 'FMT Match', key: 'suggest_fmt_trigger' }]
                    : []),
                { label: 'Channel', key: 'channel' },
                { label: 'Partner', key: 'partner' },
                ...(showAssets ? [{ label: 'Assets', key: 'assets' }] : []),
                { label: 'Date', key: 'date' },
            ];

            if (providers && providers.length > 0) {
                providers.map((provider) => {
                    const data = {
                        ...(hideNpi_id ? {} : { npi: provider?.npi_id }),

                        name: `${provider?.first_name} ${provider?.last_name}`,

                        location: only_state_zip
                            ? `${provider?.state}, ${provider?.zip}`
                            : `${provider?.address1}, ${provider?.city}, ${provider?.state}, ${provider?.zip}${
                                  showDistrict.includes(company.id)
                                      ? `, District: ${provider?.district}`
                                      : ''
                              }`,

                        ...(showDistrict.includes(company.id)
                            ? { district: provider?.district }
                            : {}),

                        ...(showTerritories.includes(company.id)
                            ? { territory: provider?.territory }
                            : {}),

                        ...(showSamTerritories.includes(company.id)
                            ? { territory1: provider?.territory1 }
                            : {}),

                        ...(isHidden ? {} : { specialty: provider?.specialty }),

                        physicianSegment: Array.isArray(provider?.segmentation)
                            ? provider?.segmentation.join(', ')
                            : provider?.segmentation,

                        ...(showAffiliationType
                            ? { affiliationType: provider?.affiliation_type || '' }
                            : {}),

                        ...(showTier ? { tier: provider?.tier } : {}),

                        ...(showFMT || showDigitalEngagementData
                            ? {
                                  digital_engagement_priority:
                                      provider?.digital_engagement_priority,
                              }
                            : {}),

                        ...(showFMT
                            ? {
                                  suggest_fmt_trigger:
                                      provider?.suggest_fmt_trigger,
                              }
                            : {}),

                        channel: provider?.channels_engaged?.join(', ') || '',
                        partner: provider?.vendor_engaged?.join(', ') || '',

                        ...(showAssets
                            ? { assets: provider?.assets?.join(', ') || '' }
                            : {}),

                        date:
                            provider?.channels_engaged_date
                                ?.map((date) => formatDateForExport(date))
                                .join(', ') || '',
                    };

                    csvData.push(data);
                });
            }

            return {
                data: csvData,
                headers: headers,
                filename: csvName + '.csv',
                target: '_blank',
            };
        }
    };
    const handlePrescriberProfileModal = (npiId) => {
        if (company.id === REGENERON_COMPANY_ID) {
            dispatch(setSelectedNPI(npiId));
            handleShowModal();
        }
    };
    const handleShowModal = () => {
        setShowModal(!showModal);
    };
    const handleShowCombineModal = (data) => {
        setCombineData(data);
        setShowCombineModal(!showCombineModal);
    };

    const finalNotes = notes[company.id] || notes.default;

    const renderNoTableData = () => (
        <TableRow>
            <TableCell colSpan={8}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                        No Providers data to display.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    // When the API paginates, `sortedData` IS the page -- slicing it again would
    // show rows 10-20 of a 10-row array, i.e. an empty table from page 2 on.
    const pageRows = serverPaginated
        ? sortedData
        : sortedData.slice(currentPage * pageSize - pageSize, currentPage * pageSize);

    const renderTableData = () =>
        totalRecordsFetch > 0
            ? pageRows
                  ?.map((row, index) => (
                      <>
                          <TableRow
                              className={handleRowClassForEngagementsTable(
                                  index,
                                  company.id,
                              )}
                              key={row?.npi_id}
                          >
                              {hideNpi_id ? null : (
                                  <TableCell
                                      className="table-row-data no-border cursor-pointer"
                                      rowSpan={2}
                                      onClick={() =>
                                          handlePrescriberProfileModal(
                                              row?.npi_id,
                                          )
                                      }
                                  >
                                      {row?.npi_id}
                                  </TableCell>
                              )}

                              <TableCell
                                  className="table-row-data no-border"
                                  rowSpan={2}
                              >
                                  {row?.first_name + ' ' + row?.last_name}
                              </TableCell>
                              {/* Location and District */}
                              <TableCell
                                  className="table-row-data no-table-border-cell gray-900 no-border w-10"
                                  rowSpan={1}
                              >
                                  {only_state_zip
                                      ? `${row?.state}, ${row?.zip}`
                                      : `${row?.address1}, ${row?.city}, ${row?.state}, ${row?.zip}`}
                              </TableCell>
                              {showDistrict.includes(company.id) && (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.district}
                                  </TableCell>
                              )}
                              {showTerritories.includes(company.id) && (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.territory}
                                  </TableCell>
                              )}
                              {showSamTerritories.includes(company.id) && (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.territory1}
                                  </TableCell>
                              )}
                              {!handleHideSpecialtyColumns(company.id) ? (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.specialty}
                                  </TableCell>
                              ) : null}
                              <TableCell
                                  className="table-row-data no-border"
                                  rowSpan={2}
                              >
                                  <Box className="npi-table-segmentation-cell">
                                      {Array.isArray(row?.segmentation)
                                          ? row?.segmentation.join(', ')
                                          : row?.segmentation}
                                  </Box>
                              </TableCell>
                              {showAffiliationType && (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.affiliation_type || ''}
                                  </TableCell>
                              )}
                              {showTier && (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      {row?.tier}
                                  </TableCell>
                              )}
                              {showFMT || showDigitalEngagementData ? (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      <Box className="flex-row-start-center gap-12">
                                          <Avatar
                                              alt={
                                                  handleIcons(
                                                      row?.digital_engagement_priority,
                                                  ).icon
                                              }
                                              className={`trigger-table-avatar ${handleIcons(row?.digital_engagement_priority).color}`}
                                              src={' '}
                                          />
                                          {row?.digital_engagement_priority}
                                      </Box>
                                  </TableCell>
                              ) : null}
                              {showFMT ? (
                                  <TableCell
                                      className="table-row-data no-border"
                                      rowSpan={2}
                                  >
                                      <Box className="flex-row-start-center gap-12">
                                          <Avatar
                                              alt={
                                                  handleIcons(
                                                      row?.suggest_fmt_trigger,
                                                  ).icon
                                              }
                                              className={`trigger-table-avatar ${handleIcons(row?.suggest_fmt_trigger).color}`}
                                              src={' '}
                                          />
                                          {row?.suggest_fmt_trigger}
                                      </Box>
                                  </TableCell>
                              ) : null}
                              <TableCell
                                  className="table-row-data no-border min-w-200px"
                                  rowSpan={2}
                              >
                                  {(row?.channels_engaged ?? [])
                                      .slice(0, 2)
                                      .map((channel, c_index) => (
                                          <span key={c_index}>
                                              {channel}
                                              {c_index > 1 ? ',' : ''}
                                              <br />
                                          </span>
                                      ))}

                                  {(row?.channels_engaged?.length ?? 0) > 2 && (
                                      <span
                                          className="text-blue-500 cursor-pointer primary-color"
                                          onClick={() =>
                                              handleShowCombineModal({
                                                  channels_engaged:
                                                      row?.channels_engaged,
                                                  vendor_engaged:
                                                      row?.vendor_engaged,
                                                  assets: row?.assets,
                                                  channels_engaged_date:
                                                      row?.channels_engaged_date,
                                              })
                                          }
                                      >
                                          View More
                                      </span>
                                  )}
                              </TableCell>
                              <TableCell
                                  className="table-row-data no-border min-w-200px"
                                  rowSpan={2}
                              >
                                  {(row?.vendor_engaged ?? [])
                                      .slice(0, 2)
                                      .map((vendor, v_index) => (
                                          <span key={v_index}>
                                              {vendor}
                                              {v_index > 1 ? ',' : ''}
                                              <br />
                                          </span>
                                      ))}

                                  {(row?.vendor_engaged?.length ?? 0) > 2 && (
                                      <span
                                          className="text-blue-500 cursor-pointer primary-color"
                                          onClick={() =>
                                              handleShowCombineModal({
                                                  channels_engaged:
                                                      row?.channels_engaged,
                                                  vendor_engaged:
                                                      row?.vendor_engaged,
                                                  assets: row?.assets,
                                                  channels_engaged_date:
                                                      row?.channels_engaged_date,
                                              })
                                          }
                                      >
                                          View More
                                      </span>
                                  )}
                              </TableCell>

                              {showAssets && (
                                  <TableCell
                                      className="table-row-data no-border min-w-200px"
                                      rowSpan={2}
                                  >
                                      {(row?.assets ?? [])
                                          .slice(0, 2)
                                          .map((asset, a_index) => (
                                              <span key={a_index}>
                                                  {asset}
                                                  {a_index > 1 ? ',' : ''}
                                                  <br />
                                              </span>
                                          ))}

                                      {(row?.assets?.length ?? 0) > 2 && (
                                          <span
                                              className="text-blue-500 cursor-pointer primary-color"
                                              onClick={() =>
                                                  handleShowCombineModal({
                                                      channels_engaged:
                                                          row?.channels_engaged,
                                                      vendor_engaged:
                                                          row?.vendor_engaged,
                                                      assets: row?.assets,
                                                      channels_engaged_date:
                                                          row?.channels_engaged_date,
                                                  })
                                              }
                                          >
                                              View More
                                          </span>
                                      )}
                                  </TableCell>
                              )}

                              <TableCell
                                  className="table-row-data no-border min-w-200px"
                                  rowSpan={2}
                              >
                                  {(row?.channels_engaged_date ?? [])
                                      .slice(0, 2)
                                      .map((date, d_index) => (
                                          <span key={d_index}>
                                              {date}
                                              {d_index > 1 ? ',' : ''}
                                              <br />
                                          </span>
                                      ))}

                                  {(row?.channels_engaged_date?.length ?? 0) >
                                      2 && (
                                      <span
                                          className="text-blue-500 cursor-pointer primary-color"
                                          onClick={() =>
                                              handleShowCombineModal({
                                                  channels_engaged:
                                                      row?.channels_engaged,
                                                  vendor_engaged:
                                                      row?.vendor_engaged,
                                                  assets: row?.assets,
                                                  channels_engaged_date:
                                                      row?.channels_engaged_date,
                                              })
                                          }
                                      >
                                          View More
                                      </span>
                                  )}
                              </TableCell>

                              {/* <TableCell className='table-row-data no-border' rowSpan={2}>
                        <img src={ActionsIcon} height={20} width={20} />
                    </TableCell> */}
                          </TableRow>
                          <TableRow
                              className={
                                  index % 2 === 0 ? 'odd-row-background' : ''
                              }
                              key={row.npi + '_district'}
                          >
                              <TableCell
                                  className="table-row-data padding-0 no-border"
                                  rowSpan={1}
                              >
                                  {!hideDistrict.includes(company.id) ? (
                                      <>
                                          <span className="font-weight-700">
                                              District:{' '}
                                          </span>
                                          {row.district}
                                      </>
                                  ) : null}
                              </TableCell>
                          </TableRow>
                      </>
                  ))
            : renderNoTableData();
    useEffect(() => {
        const handleResize = () => {
            setMaxWidth(calculateMaxWidth());
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const columnSize =
        ((!handleHideSpecialtyColumns(company.id) && showFMT) ||
        (isDistrictVisible && isTerritoryVisible)
            ? 10
            : !handleHideSpecialtyColumns(company.id) &&
                showFMT === false &&
                !showDigitalEngagementData
              ? 8
              : showDigitalEngagementData
                ? 9
                : 7) + (showAffiliationType ? 1 : 0);
    return (
        <Box className="b1px-border-box-br8px" sx={{ maxWidth: maxWidth, width: '100%', minWidth: 0 }}>
            {/* Table Header */}
            <Box className="padding-tb-20-rl-24-bb-1px">
                <Box className="flex-row-space-between-center">
                    <Box>
                        <Typography className="text-500fw-18fs-28lh">
                            See Full List Below
                        </Typography>
                    </Box>
                    {hideDownloadButtonCompanies.includes(company.id) &&
                    !is_admin ? null : (
                        <Box>
                            <Box onClick={handleExportAll} style={{ cursor: isExporting ? 'wait' : 'pointer' }}>
                                <CustomButton
                                    buttonColor="#AD509E"
                                    icon={<DownloadIcon />}
                                    title={isExporting ? 'Preparing…' : 'Export All Data'}
                                    titleColor="#FFFFFF"
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Table Content */}
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer
                    className="no-box-shadow no-border"
                    sx={{ overflowX: 'auto' }}
                    component={Paper}
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead>
                            <TableRow>
                                {hideNpi_id ? null : (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={orderBy === 'npi_id'}
                                            direction={
                                                orderBy === 'npi_id'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'npi_id',
                                            )}
                                        >
                                            NPI
                                        </TableSortLabel>
                                    </TableCell>
                                )}

                                <TableCell className="table-headers-title">
                                    <TableSortLabel
                                        active={orderBy === 'first_name'}
                                        direction={
                                            orderBy === 'first_name'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(
                                            'first_name',
                                        )}
                                    >
                                        Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="table-headers-title-no-side-padding">
                                    <TableSortLabel
                                        active={orderBy === 'address1'}
                                        direction={
                                            orderBy === 'address1'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler('address1')}
                                    >
                                        Location
                                    </TableSortLabel>
                                </TableCell>
                                {showDistrict.includes(company.id) && (
                                    <TableCell className="table-headers-title-no-side-padding">
                                        <TableSortLabel
                                            active={orderBy === 'district'}
                                            direction={
                                                orderBy === 'district'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'district',
                                            )}
                                        >
                                            Region - RMD
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {showTerritories.includes(company.id) && (
                                    <TableCell className="table-headers-title-no-side-padding">
                                        <TableSortLabel
                                            active={orderBy === 'territory'}
                                            direction={
                                                orderBy === 'territory'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'territory',
                                            )}
                                        >
                                            Territory-IROC
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {showSamTerritories.includes(company.id) && (
                                    <TableCell className="table-headers-title-no-side-padding">
                                        <TableSortLabel
                                            active={orderBy === 'territory1'}
                                            direction={
                                                orderBy === 'territory1'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'territory1',
                                            )}
                                        >
                                            Territory - SAM
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {!handleHideSpecialtyColumns(company.id) ? (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={orderBy === 'specialty'}
                                            direction={
                                                orderBy === 'specialty'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'specialty',
                                            )}
                                        >
                                            Specialty
                                        </TableSortLabel>
                                    </TableCell>
                                ) : null}
                                <TableCell className="table-headers-title">
                                    <TableSortLabel
                                        active={orderBy === 'segmentation'}
                                        direction={
                                            orderBy === 'segmentation'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(
                                            'segmentation',
                                        )}
                                    >
                                        {physician_segment_title}
                                    </TableSortLabel>
                                </TableCell>
                                {showAffiliationType && (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={
                                                orderBy === 'affiliation_type'
                                            }
                                            direction={
                                                orderBy === 'affiliation_type'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'affiliation_type',
                                            )}
                                        >
                                            Affiliation Type
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {showTier && (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={orderBy === 'tier'}
                                            direction={
                                                orderBy === 'tier'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler('tier')}
                                        >
                                            Tier
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {showFMT || showDigitalEngagementData ? (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={
                                                orderBy ===
                                                'digital_engagement_priority'
                                            }
                                            direction={
                                                orderBy ===
                                                'digital_engagement_priority'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'digital_engagement_priority',
                                            )}
                                        >
                                            Digital Engagement
                                        </TableSortLabel>
                                    </TableCell>
                                ) : null}
                                {showFMT ? (
                                    <TableCell className="table-headers-title">
                                        <TableSortLabel
                                            active={
                                                orderBy ===
                                                'suggest_fmt_trigger'
                                            }
                                            direction={
                                                orderBy ===
                                                'suggest_fmt_trigger'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'suggest_fmt_trigger',
                                            )}
                                        >
                                            FMT Match
                                        </TableSortLabel>
                                    </TableCell>
                                ) : null}
                                <TableCell className="table-headers-title">
                                    <TableSortLabel
                                        active={orderBy === 'channels_engaged'}
                                        direction={
                                            orderBy === 'channels_engaged'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(
                                            'channels_engaged',
                                        )}
                                    >
                                        Channel
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="table-headers-title">
                                    <TableSortLabel
                                        active={orderBy === 'vendor_engaged'}
                                        direction={
                                            orderBy === 'vendor_engaged'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(
                                            'vendor_engaged',
                                        )}
                                    >
                                        Partner
                                    </TableSortLabel>
                                </TableCell>
                                {showAssets && (
                                    <TableCell className="table-headers-title-no-side-padding">
                                        <TableSortLabel
                                            active={orderBy === 'assets'}
                                            direction={
                                                orderBy === 'assets'
                                                    ? order
                                                    : 'asc'
                                            }
                                            onClick={createSortHandler(
                                                'assets',
                                            )}
                                        >
                                            Assets
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                <TableCell className="table-headers-title">
                                    <TableSortLabel
                                        active={
                                            orderBy === 'channels_engaged_date'
                                        }
                                        direction={
                                            orderBy === 'channels_engaged_date'
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(
                                            'channels_engaged_date',
                                        )}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                {/* <TableCell className="table-headers-title"></TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* isLoading covers the full dashboard load;
                                isProvidersLoading covers a page turn or search,
                                which only refetches this table. */}
                            {isLoading || isProvidersLoading ? (
                                <TableRowsSkeleton
                                    columns={columnSize}
                                    rows={pageSize || 10}
                                />
                            ) : (
                                renderTableData()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Table Footer */}
            {totalPages ? (
                <Box className="padding-tb-16-rl-24 border-top-gray">
                    <Box className="flex-row-space-between-center">
                        <Box>
                            <CustomButton
                                buttonColor="#FFFFFF"
                                buttonDisabled={!(currentPage !== 1)}
                                customStyle={{ borderRadius: '8px' }}
                                handleClick={handlePreviousClick}
                                icon={<ArrowLeftIcon />}
                                title="Previous"
                                titleColor="#344054"
                            />
                        </Box>

                        {/* Pagination */}
                        <Box>
                            <Pagination
                                hideNextButton
                                hidePrevButton
                                count={totalPages}
                                page={currentPage}
                                shape="rounded"
                                onChange={handlePageChange}
                            />
                        </Box>

                        <Box>
                            <CustomButton
                                buttonColor="#FFFFFF"
                                buttonDisabled={!(totalPages > currentPage)}
                                customStyle={{ borderRadius: '8px' }}
                                handleClick={handleNextClick}
                                tailIcon={<ArrowRightIcon />}
                                title="Next"
                                titleColor="#344054"
                            />
                        </Box>
                    </Box>
                </Box>
            ) : null}
            <CustomModal
                component={<PrescribersProfile handleClose={handleShowModal} />}
                modalOpen={showModal}
                onClose={handleShowModal}
            />
            <CustomModal
                component={
                    <CombineData
                        data={combineData}
                        handleClose={handleShowCombineModal}
                        showAssets={showAssets}
                    />
                }
                modalOpen={showCombineModal}
                onClose={handleShowCombineModal}
            />
            <SmallNotes notes={finalNotes} />
        </Box>
    );
}

export default NPITable;
